<template>
  <div class="d-flex flex-column gap-3">
    <div class="d-flex justify-content-between align-items-center p-2 bg-light rounded border flex-wrap gap-2">
      <div>
        <h5 class="mb-0">Role</h5>
        <small class="text-muted">Zarządzanie rolami i uprawnieniami</small>
      </div>
      <div class="d-flex align-items-center gap-2">
        <BFormSelect v-model="selectedGroup" :options="roleGroupOptions" size="sm" />
        <BButton v-if="canManageRolesAccess" variant="success" size="sm" @click="openCreateForm">
          <i class="fas fa-plus-circle me-1"></i> Dodaj rolę
        </BButton>
      </div>
    </div>

    <div v-if="!canViewRolesAccess" class="alert alert-warning mb-0">
      Brak uprawnień do przeglądania ról.
    </div>

    <CommonTable2
      v-else
      :key="tableConfig.data"
      :table="tableConfig"
      rowIdentifier="id"
      :currentPage="currentPage"
      :totalRows="totalRows"
      :totalPages="totalPages"
      :showPaginationBottom="true"
      :showPaginationTop="false"
      :showMassActions="false"
      :multiselect="false"
      :showRowActions="true"
      @onPageChange="handlePageChange"
      @onSort="handleSort"
      @onItemAction="handleItemAction"
      :per-page="pageSize"
    />

    <RoleFormModal
      v-if="showRoleForm"
      v-model:showModal="showRoleForm"
      :role="selectedRole"
      :privilegesCatalog="privilegesCatalog"
      @onSaved="handleRolesUpdated"
    />
    <RolePrivilegesModal
      v-if="showPrivilegesModal"
      v-model:showModal="showPrivilegesModal"
      :role="selectedRole"
      @onSaved="handleRolesUpdated"
    />
  </div>
</template>

<script>
import { BButton, BFormSelect } from "bootstrap-vue-next";
import CommonTable2 from "@/components/widgets/CommonTable2.vue";
import RoleFormModal from "@/views/UserCommonManager/components/RoleFormModal.vue";
import RolePrivilegesModal from "@/views/UserCommonManager/components/RolePrivilegesModal.vue";
import { useUserRbacManagerModule } from "@/state/pinia/userRbacManagerModule";
import { canManageRoles, canViewRoles } from "@/helpers/rbacAccess";
import Swal from "sweetalert2";

export default {
  name: "RolesManagerView",
  components: { CommonTable2, RoleFormModal, RolePrivilegesModal, BButton, BFormSelect },
  data() {
    return {
      currentPage: 1,
      pageSize:20,
      totalRows: 0,
      totalPages: 1,
      sortColumn: "id",
      sortingDirection: "ASC",
      selectedGroup: "",
      roles: [],
      privilegesCatalog: [],
      selectedRole: null,
      showRoleForm: false,
      showPrivilegesModal: false,
      roleGroupOptions: [
        { value: "", text: "Wszystkie grupy" },
        { value: "ROLE_USER", text: "ROLE_USER" },
        { value: "ROLE_MODERATOR", text: "ROLE_MODERATOR" },
        { value: "ROLE_ADMIN", text: "ROLE_ADMIN" },
      ],
      tableConfig: {
        headers: [
          { key: "name", text: "Nazwa", type: "text", sortable: true, width: "200px" },
          { key: "description", text: "Opis", type: "text", sortable: false, width: "260px" },
          { key: "roleGroup", text: "Grupa", type: "text", sortable: true, width: "160px" },
          { key: "privilegesCount", text: "Uprawnienia", type: "number", sortable: false, width: "120px" },
          { key: "actions", text: "Akcje", type: "actions", sortable: false, width: "140px" },
        ],
        data: [],
      },
    };
  },
  computed: {
    rbacModule() {
      return useUserRbacManagerModule();
    },
    canViewRolesAccess() {
      return canViewRoles();
    },
    canManageRolesAccess() {
      return canManageRoles();
    },
  },
  watch: {
    selectedGroup() {
      this.currentPage = 1;
      this.refreshRolesList();
    },
  },
  methods: {
    handlePageChange(page) {
      this.currentPage = page;
      this.refreshRolesList();
    },
    handleSort(sortingKeyValue) {
      this.sortColumn = sortingKeyValue;
      this.currentPage = 1;
      this.refreshRolesList();
    },
    openCreateForm() {
      this.selectedRole = {};
      this.showRoleForm = true;
      this.loadPrivilegesCatalog();
    },
    handleItemAction(event) {
      if (event.action.action === "edit-role") {
        if (event.itemData.name === "ROLE_ADMIN") return;
        this.selectedRole = event.itemData;
        this.showRoleForm = true;
      } else if (event.action.action === "manage-privileges") {
        this.selectedRole = event.itemData;
        this.showPrivilegesModal = true;
      } else if (event.action.action === "delete-role") {
        this.confirmDeleteRole(event.itemData);
      }
    },
    confirmDeleteRole(role) {
      if (!this.canManageRolesAccess) return;
      if (role.name === "ROLE_ADMIN") return;
      Swal.fire({
        title: "Usunąć rolę?",
        text: `Czy na pewno chcesz usunąć rolę "${role.name}"?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc3545",
        confirmButtonText: "Usuń",
        cancelButtonText: "Anuluj",
      }).then((result) => {
        if (result.isConfirmed) {
          this.rbacModule.httpDeleteRole({ id: this.resolveRoleId(role) }).then(() => {
            this.refreshRolesList();
          });
        }
      });
    },
    resolveRoleId(role) {
      return role.id || role.roleId || role.guid;
    },
    loadPrivilegesCatalog() {
      if (this.privilegesCatalog.length > 0) return;
      this.rbacModule.httpGetPrivileges().then(() => {
        this.privilegesCatalog = [...this.rbacModule.getPrivilegesCatalog];
      });
    },
    buildRoleRows() {
      this.tableConfig.data = this.roles.map((role) => {
        const privileges = role.privilegeKeys || role.privileges || [];
        const privilegeCount = Array.isArray(privileges) ? privileges.length : 0;
        const isAdmin = role.name === "ROLE_ADMIN";
        return {
          ...role,
          id: this.resolveRoleId(role),
          privilegesCount: privilegeCount,
          actions: [
            {
              icon: "fas fa-edit",
              action: "edit-role",
              tooltip: isAdmin ? "Rola systemowa – tylko podgląd" : "Edytuj",
              enabled: this.canManageRolesAccess && !isAdmin,
              class: "btn btn-sm btn-outline-primary",
            },
            {
              icon: "fas fa-shield-alt",
              action: "manage-privileges",
              tooltip: "Uprawnienia",
              enabled: this.canManageRolesAccess,
              class: "btn btn-sm btn-outline-secondary",
            },
            {
              icon: "fas fa-trash-alt",
              action: "delete-role",
              tooltip: isAdmin ? "Rola systemowa – nie można usunąć" : "Usuń",
              enabled: this.canManageRolesAccess && !isAdmin,
              class: "btn btn-sm btn-outline-danger",
            },
          ],
        };
      });
    },
    handleRolesUpdated() {
      this.refreshRolesList();
    },
    refreshRolesList() {
      if (!this.canViewRolesAccess) return;
      this.rbacModule
        .httpGetRolesPaging({
          currentPage: this.currentPage,
          sortingKey: this.sortColumn,
          sortingDirection: this.sortingDirection,
          group: this.selectedGroup || undefined,
          pageSize:this.pageSize
        })
        .then(() => {
          this.roles = [...this.rbacModule.getRoles];
          this.totalPages = this.rbacModule.getRolesPagesCount || 1;
          this.totalRows = this.rbacModule.getRolesDataTotal || 0;
          console.log("Roles fetched ",this.roles);
          this.buildRoleRows();
        });
    },
  },
  mounted() {
    this.refreshRolesList();
  },
};
</script>
